import { z } from "zod";
import { isValidTransactionDate } from "../transactions/schema";

export const MEMBER_STATUSES = ["BASIC", "ACTIVE", "LOCKED", "CLOSED"] as const;

export const memberSearchFormSchema = z.object({
  from: z.string().refine(isValidTransactionDate, "유효한 가입 시작일을 선택해 주세요.").optional(),
  to: z.string().refine(isValidTransactionDate, "유효한 가입 종료일을 선택해 주세요.").optional(),
  channelCode: z.string().optional(),
  planCode: z.string().optional(),
  keyword: z.string().max(100, "검색어는 100자 이하로 입력해 주세요."),
  status: z.union([z.literal(""), z.enum(MEMBER_STATUSES)]),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) context.addIssue({ code: "custom", message: "가입 종료일은 시작일보다 빠를 수 없습니다.", path: ["to"] });
});

export type MemberSearchFormValues = z.infer<typeof memberSearchFormSchema>;

export function parseMemberStatus(value: string | null): MemberSearchFormValues["status"] {
  const parsed = z.union([z.literal(""), z.enum(MEMBER_STATUSES)]).safeParse(value ?? "");
  return parsed.success ? parsed.data : "";
}

export const memberNoteFormSchema = z.object({
  content: z.string().trim().min(1, "상담 메모를 입력해 주세요.").max(2_000, "상담 메모는 2,000자 이하로 입력해 주세요."),
});

export type MemberNoteFormValues = z.infer<typeof memberNoteFormSchema>;

export const terminationFormSchema = z.object({
  reason: z.string().trim().min(1, "해지 사유를 입력해 주세요.").max(1_000, "해지 사유는 1,000자 이하로 입력해 주세요."),
  bankCode: z.string().trim().max(20, "은행 코드는 20자 이하로 입력해 주세요."),
  accountNumber: z.string().trim().refine(
    (value) => value === "" || /^\d{8,20}$/.test(value),
    "계좌번호는 숫자 8~20자리로 입력해 주세요.",
  ),
  accountHolder: z.string().trim().max(100, "예금주는 100자 이하로 입력해 주세요."),
  agreedToRefundPolicy: z.boolean().refine(Boolean, "환불 정책을 확인해 주세요."),
  confirmed: z.boolean().refine(Boolean, "해지 요청 내용을 확인해 주세요."),
}).superRefine((values, context) => {
  const hasAccountValue = Boolean(values.bankCode || values.accountNumber || values.accountHolder);
  if (!hasAccountValue) return;
  if (!values.bankCode) context.addIssue({ code: "custom", message: "은행 코드를 입력해 주세요.", path: ["bankCode"] });
  if (!values.accountNumber) context.addIssue({ code: "custom", message: "계좌번호를 입력해 주세요.", path: ["accountNumber"] });
  if (!values.accountHolder) context.addIssue({ code: "custom", message: "예금주를 입력해 주세요.", path: ["accountHolder"] });
});

export type TerminationFormValues = z.infer<typeof terminationFormSchema>;

export const SENSITIVE_MEMBER_FIELDS = ["PHONE", "NAME", "BIRTH_DATE", "EMAIL"] as const;
export type SensitiveMemberField = (typeof SENSITIVE_MEMBER_FIELDS)[number];

export const sensitiveRevealSchema = z.object({
  fields: z.array(z.enum(SENSITIVE_MEMBER_FIELDS)).min(1, "열람할 항목을 하나 이상 선택해 주세요."),
  reason: z.string().trim().min(5, "열람 사유를 5자 이상 입력해 주세요.").max(500, "열람 사유는 500자 이하로 입력해 주세요."),
});
